import { AxiosResponse } from 'axios';
import { all, call, fork, put, takeLatest } from 'redux-saga/effects';

import {
  PlanProgressGraphApiResponse,
  PlanProgressGraphFieldItem,
  PlanProgressGraphParams,
  PlanProgressTransitionGraph,
  toPlanProgressGraphSetting,
} from '../domain/dataPlanProgressGraph';
import {
  normalizeDataPlanProgressGraphFieldsForFetch,
  resolveDataPlanProgressGraphFields,
} from '../domain/dataPlanProgressGraphFields';

import {
  DataPlanProgressTransitionGraphActionCreators,
  DataPlanProgressTransitionGraphActionTypes,
  FetchDataPlanProgressGraphAction,
  SearchDataPlanProgressTransitionGraphFieldsAction,
  SetDataPlanProgressTransitionGraphFieldsAction,
  dataPlanProgressTransitionGraphSearchConditionSelector,
} from '../redux/server/dataPlanProgressTransitionGraph';
import { Api, buildConfig } from '../utils/api';
import { customSelect } from '../utils/customSelect';
import { handleErrorSaga } from './errorSaga';

type GraphFieldFetchResult =
  | { field: string; response: PlanProgressGraphApiResponse }
  | { field?: string; error: unknown };

const isGraphFieldFetchSuccess = (
  result: GraphFieldFetchResult
): result is { field: string; response: PlanProgressGraphApiResponse } =>
  'response' in result;

const isGraphFieldFetchFailure = (
  result: GraphFieldFetchResult
): result is { field?: string; error: unknown } => 'error' in result;

const toGraphApiParams = (
  params: PlanProgressGraphParams,
  field?: string
): PlanProgressGraphParams => {
  const { field: _field, fields: _fields, ...commonParams } = params;
  return field ? { ...commonParams, field } : commonParams;
};

function* fetchGraphByField(
  api: Api,
  params: PlanProgressGraphParams,
  field?: string
) {
  const response: AxiosResponse<PlanProgressGraphApiResponse> = yield call(
    api.get,
    '/data/planProgressTransition/graph',
    buildConfig(toGraphApiParams(params, field))
  );
  return response.data;
}

function* fetchGraphByFieldSafely(
  api: Api,
  params: PlanProgressGraphParams,
  field?: string
): Generator<unknown, GraphFieldFetchResult, PlanProgressGraphApiResponse> {
  try {
    const response = yield call(fetchGraphByField, api, params, field);
    const resolvedField = field ?? response.setting.field;
    return resolvedField
      ? { field: resolvedField, response }
      : {
          error: new Error(
            'Plan progress graph API response does not contain setting.field'
          ),
        };
  } catch (error: unknown) {
    return { field, error };
  }
}

const buildPlanProgressTransitionGraph = (
  results: { field: string; response: PlanProgressGraphApiResponse }[]
): PlanProgressTransitionGraph => {
  const firstResponse = results.at(0)?.response;
  if (!firstResponse) {
    throw new Error('Plan progress graph API response is empty');
  }
  const graphList: PlanProgressGraphFieldItem[] = results.map(
    ({ field, response }) => ({
      field,
      data: response.data,
    })
  );
  return {
    setting: toPlanProgressGraphSetting(firstResponse.setting),
    graphList,
  };
};

/**
 * 推移グラフデータ取得処理。
 * fields 指定時は field ごとに並列取得し、未指定の初回は API 既定項目を維持する。
 */
export function* fetchDataPlanProgressTransitionGraphSaga(
  api: Api,
  action: FetchDataPlanProgressGraphAction
) {
  const { params } = action.payload;
  yield put(
    DataPlanProgressTransitionGraphActionCreators.fetchDataPlanProgressTransitionGraphRequestAction()
  );
  const fields = resolveDataPlanProgressGraphFields(params);
  const targetFields: (string | undefined)[] = fields ?? [undefined];
  const results: GraphFieldFetchResult[] = yield all(
    targetFields.map((field) =>
      call(fetchGraphByFieldSafely, api, params, field)
    )
  );
  const successfulResults = results.filter(isGraphFieldFetchSuccess);
  const failedResults = results.filter(isGraphFieldFetchFailure);

  if (successfulResults.length === 0) {
    yield put(
      DataPlanProgressTransitionGraphActionCreators.renewDataPlanProgressTransitionGraphAction()
    );
    const firstError = failedResults.at(0)?.error;
    if (firstError !== undefined) {
      yield fork(handleErrorSaga, firstError);
    }
    return;
  }

  try {
    yield put(
      DataPlanProgressTransitionGraphActionCreators.fetchDataPlanProgressTransitionGraphSuccessAction(
        buildPlanProgressTransitionGraph(successfulResults)
      )
    );
  } catch (error: unknown) {
    yield put(
      DataPlanProgressTransitionGraphActionCreators.renewDataPlanProgressTransitionGraphAction()
    );
    yield fork(handleErrorSaga, error);
    return;
  }

  if (failedResults.length > 0) {
    console.warn(
      `[dataPlanProgressTransitionGraphSagas] 一部グラフ項目の取得に失敗しました: ${failedResults
        .map((result) => result.field ?? 'default')
        .join(', ')}`
    );
  }
}

function* refetchDataPlanProgressGraphWithFields(fields: string[]) {
  const searchCondition = yield* customSelect(
    dataPlanProgressTransitionGraphSearchConditionSelector()
  );
  yield put(
    DataPlanProgressTransitionGraphActionCreators.fetchDataPlanProgressGraphAction(
      {
        ...searchCondition,
        fields: normalizeDataPlanProgressGraphFieldsForFetch(fields),
      }
    )
  );
}

/**
 * 指定したグラフ項目の推移グラフデータを再取得する（単一項目）
 */
function* searchPlanProgressTransitionGraphFieldsSaga(
  action: SearchDataPlanProgressTransitionGraphFieldsAction
) {
  const { field } = action.payload;
  if (!field) {
    return;
  }
  yield* refetchDataPlanProgressGraphWithFields([field]);
}

function* setDataPlanProgressTransitionGraphFieldsSaga(
  action: SetDataPlanProgressTransitionGraphFieldsAction
) {
  yield* refetchDataPlanProgressGraphWithFields(action.payload.fields);
}

function* handleFetchPlanProgressTransitionGraphSaga(api: Api) {
  yield takeLatest(
    DataPlanProgressTransitionGraphActionTypes.FETCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH,
    fetchDataPlanProgressTransitionGraphSaga,
    api
  );
}

function* handleSearchSaga() {
  yield takeLatest(
    DataPlanProgressTransitionGraphActionTypes.SEARCH_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_FIELDS,
    searchPlanProgressTransitionGraphFieldsSaga
  );
  yield takeLatest(
    DataPlanProgressTransitionGraphActionTypes.SET_DATA_PLAN_PROGRESS_TRANSITION_GRAPH_FIELDS,
    setDataPlanProgressTransitionGraphFieldsSaga
  );
}

export function* dataPlanProgressTransitionGraphSaga(context: { api: Api }) {
  yield fork(handleFetchPlanProgressTransitionGraphSaga, context.api);
  yield fork(handleSearchSaga);
}
